import type { Workbook, Worksheet } from "exceljs";
import type { DateTime } from "luxon";
import type { Department } from "../../../contexts/AuthProvider";
import type { CensusPointInTimes, DailyPrintoutGrouping, PrintoutSchedule } from "../../../utils/models";
import { createDayViewWithoutFloorsSummary } from "../../downloadDetailedDayView";

interface CreateDayViewWithoutFloorsArgs {
	workbook: Workbook;
	hospitalData: PrintoutSchedule;
	weekIndex: number;
	dayIndex: number;
	department: Department;
	day: string;
	date: DateTime;
	printNotes: boolean;
	printoutGroupBy: DailyPrintoutGrouping;
	showDhppd: boolean;
	showNhppd: boolean;
	showActualStaffColumn: boolean;
	showActualHoursColumn: boolean;
	dhppd: number;
	nhppd: number;
	dayViewCensus: number;
	dayViewNursingHours: number;
	dayViewHppd: number;
	jobManagementEnabled: boolean;
	censusPointInTimes: CensusPointInTimes | undefined;
}

/**
 * Summary download — a stripped-down rollup that bypasses the detailed pipeline (mapping §1 mode branch).
 *
 * Until the summary pipeline is rewritten in this folder, this thin shim delegates to the
 * legacy `createDayViewWithoutFloorsSummary` in `downloadDetailedDayView.tsx`. The detailed
 * pipeline (the much larger surface) is fully owned by the new folder; only the small summary
 * rollup keeps using the legacy implementation.
 */
export const createDayViewWithoutFloors = async (args: CreateDayViewWithoutFloorsArgs): Promise<void> => {
	const worksheet: Worksheet = args.workbook.addWorksheet("Daily Schedule");
	return createDayViewWithoutFloorsSummary(
		worksheet,
		args.hospitalData,
		args.weekIndex,
		args.dayIndex,
		args.department,
		args.day,
		args.date,
		args.printNotes,
		args.printoutGroupBy,
		{
			showDhppd: args.showDhppd,
			showNhppd: args.showNhppd,
			showActualStaffColumn: args.showActualStaffColumn,
			showActualHoursColumn: args.showActualHoursColumn,
			dhppd: args.dhppd,
			nhppd: args.nhppd,
			dayViewCensus: args.dayViewCensus,
			dayViewNursingHours: args.dayViewNursingHours,
			dayViewHppd: args.dayViewHppd,
			censusPointInTimes: args.censusPointInTimes,
		},
		args.jobManagementEnabled,
	);
};
